import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import { toast } from "sonner";
import { Disc, Square, Loader2 } from "lucide-react";
import { MeetingRecorder, isRecordingSupported, REC_TOPIC } from "../lib/meetingRecorder";

const fmt = (sec) => {
  const p = (n) => String(n).padStart(2, "0");
  const hh = Math.floor(sec / 3600);
  return `${hh > 0 ? hh + ":" : ""}${p(Math.floor((sec % 3600) / 60))}:${p(sec % 60)}`;
};

/** Rendered INSIDE <LiveKitRoom>. Shows the 🔴 REC banner to every web
 * participant and, for the meeting organiser, the record/stop button.
 * Recording is fully client-side (free) — saved straight to the local disk. */
export const MeetingRecordingLayer = ({ canRecord, title }) => {
  const { t } = useTranslation();
  const room = useRoomContext();
  const recorderRef = useRef(null);
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [remoteRec, setRemoteRec] = useState(null); // { by, lastSeen }

  // Listen for recording heartbeats from other participants.
  useEffect(() => {
    if (!room) return undefined;
    const onData = (payload, _participant, _kind, topic) => {
      if (topic !== REC_TOPIC) return;
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg?.t !== "rec") return;
        setRemoteRec(msg.on ? { by: msg.by || "", lastSeen: Date.now() } : null);
      } catch {}
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room]);

  // 1s ticker: local elapsed + expire stale remote heartbeats (>10s).
  useEffect(() => {
    const id = setInterval(() => {
      if (recorderRef.current?.recording) setElapsed(recorderRef.current.getElapsedSec());
      setRemoteRec((r) => (r && Date.now() - r.lastSeen > 10000 ? null : r));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Warn before closing the tab mid-recording + stop cleanly on unmount.
  useEffect(() => {
    if (!rec) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [rec]);
  useEffect(
    () => () => {
      recorderRef.current?.stop().catch(() => {});
    },
    [],
  );

  const start = async () => {
    if (busy || rec) return;
    if (!isRecordingSupported()) {
      toast.error(t("meetingsPage.recUnsupported", "Browserul tău nu suportă înregistrarea — folosește Chrome sau Edge."));
      return;
    }
    setBusy(true);
    try {
      const r = new MeetingRecorder(room, { title });
      await r.start();
      recorderRef.current = r;
      setElapsed(0);
      setRec(true);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(String(err?.message || err));
      }
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (busy || !recorderRef.current) return;
    setBusy(true);
    try {
      const res = await recorderRef.current.stop();
      if (res?.fileName) {
        toast.success(t("meetingsPage.recSaved", { name: res.fileName, defaultValue: `Înregistrarea a fost salvată: ${res.fileName}` }));
      }
    } catch (err) {
      toast.error(String(err?.message || err));
    } finally {
      recorderRef.current = null;
      setRec(false);
      setBusy(false);
    }
  };

  const bannerBy = rec ? room?.localParticipant?.name || "" : remoteRec?.by || "";
  const showBanner = rec || !!remoteRec;

  return (
    <>
      {showBanner && (
        <div
          data-testid="rec-banner"
          style={{
            position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
            zIndex: 60, display: "flex", alignItems: "center", gap: 8,
            padding: "6px 14px", borderRadius: 999,
            background: "rgba(120, 20, 20, 0.92)", border: "1px solid rgba(255,255,255,0.25)",
            color: "#fff", fontSize: "0.78rem", fontWeight: 600, letterSpacing: "0.06em",
            pointerEvents: "none", whiteSpace: "nowrap", maxWidth: "90%", overflow: "hidden",
          }}
        >
          <span
            style={{
              width: 9, height: 9, borderRadius: "50%", background: "#ff5252",
              animation: "sc-rec-blink 1.4s infinite", flexShrink: 0,
            }}
          />
          {t("meetingsPage.recBanner", "Ședința este înregistrată")}
          {bannerBy ? ` — ${t("meetingsPage.recBy", { name: bannerBy, defaultValue: `de ${bannerBy}` })}` : ""}
          {rec ? ` · ${fmt(elapsed)}` : ""}
        </div>
      )}
      {canRecord && (
        <button
          type="button"
          onClick={rec ? stop : start}
          disabled={busy || (!rec && !!remoteRec)}
          data-testid={rec ? "btn-stop-recording" : "btn-record-meeting"}
          title={rec ? t("meetingsPage.recStopBtn", "OPREȘTE ÎNREGISTRAREA") : t("meetingsPage.recBtn", "ÎNREGISTREAZĂ")}
          style={{
            position: "absolute", top: 10, right: 10, zIndex: 60,
            display: "flex", alignItems: "center", gap: 6,
            padding: "7px 13px", borderRadius: 999, cursor: "pointer",
            fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em",
            border: rec ? "1px solid #ff8a80" : "1px solid #C8A96A",
            background: rec ? "rgba(120, 20, 20, 0.92)" : "rgba(11, 22, 32, 0.85)",
            color: rec ? "#fff" : "#e8d59a",
            opacity: busy || (!rec && !!remoteRec) ? 0.55 : 1,
          }}
        >
          {busy ? (
            <Loader2 size={13} className="animate-spin" />
          ) : rec ? (
            <Square size={12} fill="currentColor" />
          ) : (
            <Disc size={13} color="#ff5252" />
          )}
          {rec
            ? `${t("meetingsPage.recStopBtn", "OPREȘTE ÎNREGISTRAREA")} · ${fmt(elapsed)}`
            : t("meetingsPage.recBtn", "ÎNREGISTREAZĂ")}
        </button>
      )}
      <style>{`@keyframes sc-rec-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }`}</style>
    </>
  );
};
